import React, {
  useEffect, useMemo, useState,
} from 'react';
import {
  Box, Button, Checkbox, Dialog, DialogContent, IconButton, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography, useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RankBadge from '../gameScoreBoard/RankBadge';
import ScoreboardDetailModal from '../gameScoreBoard/ScoreboardDetailModal';
import ConfirmModal from '../form-components/ConfirmModal';
import LocalText from '../common/LocalText';
import { useToast } from '../../context/ToastContext';
import {
  getEligiblePlayers, grantPrizes, getWinners, getPrizeCatalog, getPrizePackItems,
} from '../../api/gameAnnouncementApi';
import { getItemPicker, getItemTypes } from '../../api/itemApi';
import { C, entityColor, btnBase, ctaNavy } from '../../theme/adminTokens';
import { initialOf } from '../../utils/avatarColor';
import { announcementQuizzes } from '../../utils/gameSchedule';
import PrizeItemPickerModal from './PrizeItemPickerModal';
import AnnouncementPlayerBreakdownModal from './AnnouncementPlayerBreakdownModal';

// Avatars are never a grantable prize — same exclusion AnnouncementFormModal.jsx applies.
const AVATAR_ITEM_TYPE_ID = 6;

const catalogItemLabel = (item) => item?.name || `Item #${item?.itemId}`;

const fmtDate = (val) => {
  if (!val) return '—';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
};

const STEPS = [
  { n: 1, label: 'Choose Mode' },
  { n: 2, label: 'Assign Prizes' },
  { n: 3, label: 'Review & Grant' },
];

const headCellSx = {
  fontSize: 11,
  fontWeight: 700,
  color: C.muted,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  // Set on the cell, not the row — table 3 below uses stickyHeader, which needs an opaque
  // background on each th itself to properly occlude scrolled-under rows.
  bgcolor: '#f1f5f9',
  borderBottom: `1px solid ${C.border}`,
  borderRight: `1px solid ${C.border}`,
  '&:last-of-type': { borderRight: 'none' },
  whiteSpace: 'nowrap',
  py: 1.5,
  px: 2,
};

const bodyCellSx = {
  fontSize: 13,
  color: '#1e293b',
  borderBottom: `1px solid ${C.border}`,
  borderRight: `1px solid ${C.border}`,
  '&:last-of-type': { borderRight: 'none' },
  py: 1.5,
  px: 2,
  verticalAlign: 'middle',
};

// Thin, styled scrollbar for these tables' own overflow — most only scroll horizontally
// (columns wider than the fixed-width dialog), but the Review & Grant table below also
// scrolls vertically (maxHeight + stickyHeader), hence both width and height here rather
// than just one. The native OS scrollbar was a thick, unstyled bar that looked out of place
// against the rest of this redesigned table.
const thinScrollbarSx = {
  '&::-webkit-scrollbar': { width: 6, height: 6 },
  '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
  '&::-webkit-scrollbar-thumb': { backgroundColor: C.border, borderRadius: 4 },
  '&::-webkit-scrollbar-thumb:hover': { backgroundColor: C.muted },
  scrollbarWidth: 'thin',
  scrollbarColor: `${C.border} transparent`,
};

// Reused as-is by the Stepper header, the mode-choice cards, and the item-picker grid so
// this file doesn't invent three different card treatments for the same "selectable tile"
// shape — see PrizeItemPickerModal's own PickerCard for the item-catalog version this
// mirrors.
function ModeCard({
  icon, title, description, onClick, disabled = false,
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-label={title}
      sx={{
        flex: 1, textAlign: 'left', bgcolor: disabled ? '#f1f5f9' : '#f8fafc',
        border: `1.5px solid ${C.border}`,
        borderRadius: '14px', p: 2.5, fontFamily: 'inherit',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        ...(!disabled && { '&:hover': { borderColor: C.navy } }),
      }}
    >
      <Box sx={{
        width: 38, height: 38, borderRadius: '10px', bgcolor: disabled ? C.muted : C.navy, color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, fontWeight: 800, mb: 1.5,
      }}
      >
        {icon}
      </Box>
      <Typography sx={{ fontSize: 14, fontWeight: 800, color: disabled ? C.muted : C.navy }}>
        <LocalText>{title}</LocalText>
      </Typography>
      <Typography sx={{ fontSize: 12, color: C.muted, mt: 0.75, lineHeight: 1.5 }}>
        <LocalText>{description}</LocalText>
      </Typography>
    </Box>
  );
}

// Numbered-circle + dashed-connector stepper — same visual language as
// AssignDepositWizard.jsx's own inline stepper (payment/deposits flow), reproduced locally
// per this codebase's convention of each feature keeping its own small copy rather than a
// shared component (see e.g. TransactionTable.jsx/DepositDetail.jsx's badge-config note).
function Stepper({ step, onStepClick }) {
  return (
    <Box sx={{
      bgcolor: '#fff', borderRadius: 3, border: '1px solid rgba(0,0,0,0.06)',
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)', p: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 1,
    }}
    >
      {STEPS.map((s, i) => {
        const isCurrent = s.n === step;
        const isDone = s.n < step;
        const clickable = s.n < step;
        return (
          <React.Fragment key={s.n}>
            <Box
              onClick={clickable ? () => onStepClick(s.n) : undefined}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.25, px: 1.5, py: 1, borderRadius: '10px',
                bgcolor: isCurrent ? '#eff6ff' : 'transparent', cursor: clickable ? 'pointer' : 'default', flexShrink: 0,
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 800, flexShrink: 0,
                bgcolor: isDone || isCurrent ? C.navy : '#eef2f6',
                color: isDone || isCurrent ? '#fff' : '#94a3b8',
              }}
              >
                {isDone ? <CheckIcon sx={{ fontSize: 16 }} /> : s.n}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography fontSize={10} fontWeight={700} sx={{ color: isCurrent ? C.navy : '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight: 1.4 }}>
                  <LocalText>Step</LocalText> {s.n}
                </Typography>
                <Typography fontSize={12.5} fontWeight={800} sx={{ color: isCurrent || isDone ? '#1e293b' : '#94a3b8', whiteSpace: 'nowrap' }}>
                  <LocalText>{s.label}</LocalText>
                </Typography>
              </Box>
            </Box>
            {i < STEPS.length - 1 && (
              <Box sx={{ flex: 1, minWidth: 24, height: 0, borderTop: '2px dashed', borderColor: s.n < step ? C.navy : C.border, mx: 1 }} />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
}

/**
 * Announcement detail: a merged Players list (rank/score/last-played/prize, one row per
 * eligible player — replaces what used to be three separate stacked lists: eligible
 * players, current assignments, winners) plus a 3-step "Assign Prizes" wizard (Choose Mode
 * -> Assign -> Review & Grant) for canManage users. Reuses ScoreboardDetailModal (the real
 * Game Scoreboard's own player drill-down) for "Details", rather than a bespoke one — except
 * for a Quiz announcement, which is scored per selected quiz (not by date), so its "Details"
 * is the per-quiz points list (AnnouncementPlayerBreakdownModal).
 *
 * Prize picks are staged CLIENT-SIDE ONLY (`pendingAssignments` state below) — revised
 * 2026-09-26 to remove the earlier standalone assign/remove/list-prizes API calls entirely.
 * Nothing is sent to the server until Grant: the one `grantPrizes` call carries the whole
 * staged list and both persists it (game_announcement_user_prize) and grants, in a single
 * request (see GameAnnouncementService.GrantWinnersAsync). Closing this dialog while
 * `pendingAssignments` is non-empty asks for confirmation first (see confirmCloseOpen below) —
 * there is no "come back later, still staged" capability anymore; that's an explicit tradeoff
 * for not round-tripping the server on every pick/remove click.
 *
 * Prize resolution at grant time is: this player's own staged assignment if any, ELSE the
 * announcement's Prize Pack (game_announcement_prize_pack) if any, ELSE no prize at all
 * ("recognition only"). game_announcement_prize_pack is deliberately NOT writable from this
 * modal at all — it's the announced menu, set only via Create/Edit Announcement. Both wizard
 * modes below only ever stage per-user (user, item) pairs restricted to items already in that
 * announced Prize Pack (never the full catalog):
 *   - "Same Prize for All Users" has no item-picking step at all — every Prize Pack item is
 *     staged for every not-yet-granted player automatically.
 *   - "Different Prizes for Different Users" lets the admin pick specific Prize Pack item(s)
 *     and specific players per group, repeatable for multiple groups.
 * The Review step always shows, per not-yet-granted player, whichever of these actually
 * applies to them (falling back to the Prize Pack label if nothing was staged) — never
 * assumes only one path was used.
 */
export default function AnnouncementDetailModal({
  open, onClose, gameId, announcement, canManage = false, onGranted,
}) {
  const { toast } = useToast();
  const isNarrow = useMediaQuery('(max-width:720px)');

  const [step, setStep] = useState(0); // 0 = merged Players list, 1-3 = the wizard
  const [mode, setMode] = useState(null); // 'same' | 'different'

  const [playersLoading, setPlayersLoading] = useState(true);
  const [players, setPlayers] = useState([]);

  // Staged locally only — never sent to the server until Grant (see this file's top doc
  // comment). Each entry: { userId, itemId, quantity, itemName }.
  const [pendingAssignments, setPendingAssignments] = useState([]);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  const [winners, setWinners] = useState([]);
  const [winnersLoading, setWinnersLoading] = useState(true);

  const [prizePack, setPrizePack] = useState([]);
  const [prizePackLoading, setPrizePackLoading] = useState(true);

  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [itemTypes, setItemTypes] = useState([]);

  const [groupItemIds, setGroupItemIds] = useState([]);
  const [groupPickerOpen, setGroupPickerOpen] = useState(false);
  const [checkedForAssign, setCheckedForAssign] = useState([]); // userId[]
  const [granting, setGranting] = useState(false);

  const [detailUserId, setDetailUserId] = useState(null);

  const announcementId = announcement?.id;

  const refreshPlayers = () => {
    if (!gameId || !announcementId) return undefined;
    setPlayersLoading(true);
    return getEligiblePlayers(gameId, announcementId)
      .then((data) => setPlayers(data?.items ?? []))
      .catch(() => toast.error('Failed to load eligible players.'))
      .finally(() => setPlayersLoading(false));
  };

  const refreshWinners = () => {
    if (!gameId || !announcementId) return undefined;
    setWinnersLoading(true);
    return getWinners(gameId, announcementId)
      .then((data) => setWinners(Array.isArray(data) ? data : []))
      .catch(() => toast.error('Failed to load winners.'))
      .finally(() => setWinnersLoading(false));
  };

  const refreshPrizePack = () => {
    if (!gameId || !announcementId) return undefined;
    setPrizePackLoading(true);
    return getPrizePackItems(gameId, announcementId)
      .then((data) => setPrizePack(Array.isArray(data) ? data : []))
      .catch(() => toast.error('Failed to load the prize pack.'))
      .finally(() => setPrizePackLoading(false));
  };

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setMode(null);
    setPendingAssignments([]);
    refreshPlayers();
    refreshWinners();
    refreshPrizePack();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, gameId, announcementId]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setCatalogLoading(true);
    Promise.all([getItemPicker().catch(() => []), getPrizeCatalog().catch(() => [])])
      .then(([fullCatalog, prizeCatalog]) => {
        if (cancelled) return;
        const merged = new Map();
        (Array.isArray(fullCatalog) ? fullCatalog : []).forEach((it) => merged.set(it.itemId, it));
        (Array.isArray(prizeCatalog) ? prizeCatalog : []).forEach((it) => merged.set(it.itemId, it));
        const withoutAvatars = [...merged.values()].filter((it) => it.itemTypeId !== AVATAR_ITEM_TYPE_ID);
        setCatalog(withoutAvatars.sort((a, b) => catalogItemLabel(a).localeCompare(catalogItemLabel(b))));
      })
      .finally(() => { if (!cancelled) setCatalogLoading(false); });
    getItemTypes().then((data) => { if (!cancelled) setItemTypes(Array.isArray(data) ? data : []); }).catch(() => {});
    return () => { cancelled = true; };
  }, [open]);

  const catalogById = useMemo(() => new Map(catalog.map((it) => [it.itemId, it])), [catalog]);
  // Per-user prize assignment must only ever grant something already announced for this
  // specific announcement — the Prize Pack IS the announced menu (set at Create/Edit, or via
  // "Same for everyone" mode below, which writes to the same place). Narrowing the picker to
  // this subset (rather than the full catalog every other picker in this feature uses) is
  // what enforces that; catalogById above stays on the full list since it's also used to
  // resolve names for already-assigned items that may no longer be in the current pack.
  const prizePackItemIds = useMemo(() => new Set(prizePack.map((p) => p.itemId)), [prizePack]);
  const prizePackCatalog = useMemo(
    () => catalog.filter((it) => prizePackItemIds.has(it.itemId)),
    [catalog, prizePackItemIds],
  );
  const grantedUserIds = useMemo(() => new Set(winners.map((w) => w.userId)), [winners]);
  const assignmentsByUser = useMemo(() => {
    const map = new Map();
    pendingAssignments.forEach((a) => {
      const list = map.get(a.userId) ?? [];
      list.push(a);
      map.set(a.userId, list);
    });
    return map;
  }, [pendingAssignments]);
  const prizePackLabel = useMemo(() => prizePack.map((p) => p.itemName).join(', '), [prizePack]);
  const pendingPlayers = useMemo(() => players.filter((p) => !grantedUserIds.has(p.userId)), [players, grantedUserIds]);

  // What THIS player would actually receive if granted right now — the same
  // assignment-then-prize-pack-then-nothing resolution the backend applies at grant time.
  const resolvedPrizeFor = (userId) => {
    const own = assignmentsByUser.get(userId);
    if (own?.length) return { label: own.map((a) => a.itemName).join(', '), source: 'assignment' };
    if (prizePack.length) return { label: prizePackLabel, source: 'prizePack' };
    return { label: null, source: 'none' };
  };

  // ── Step 0 → 1 ────────────────────────────────────────────────────────────
  const startWizard = () => {
    setMode(null);
    setGroupItemIds([]);
    setCheckedForAssign([]);
    setStep(1);
  };

  const chooseMode = (m) => {
    // Belt-and-suspenders alongside the ModeCard's own disabled state above — neither mode
    // has anything to assign from without an announced Prize Pack.
    if (prizePack.length === 0) return;
    setMode(m);
    setGroupItemIds([]);
    setCheckedForAssign([]);

    // "Same" has nothing left to pick — every pack item goes to every pending player,
    // automatically. No Step 2 for this mode; straight to staging the assignments, then
    // Review. "Different" still needs its own item+player picking step.
    if (m === 'same') {
      handleChooseSame();
      return;
    }
    setStep(2);
  };

  // Upsert semantics matching the old server-side MERGE — a (userId, itemId) pair already
  // staged just has its quantity replaced, not duplicated.
  const mergeAssignments = (prev, incoming) => {
    const map = new Map(prev.map((a) => [`${a.userId}:${a.itemId}`, a]));
    incoming.forEach((a) => map.set(`${a.userId}:${a.itemId}`, a));
    return [...map.values()];
  };

  // ── Same Prize for All Users — no picker: every pack item, every pending player ──
  // Purely local state now — no request at all, let alone one-per-player (a real API-flood
  // bug this used to have: 9 players x a handful of pack items once fired dozens of
  // simultaneous requests, before this whole feature moved off per-action network calls).
  const handleChooseSame = () => {
    if (prizePack.length === 0 || pendingPlayers.length === 0) return;
    const incoming = pendingPlayers.flatMap((p) => prizePack.map((item) => (
      { userId: p.userId, itemId: item.itemId, quantity: 1, itemName: catalogItemLabel(catalogById.get(item.itemId)) }
    )));
    setPendingAssignments((prev) => mergeAssignments(prev, incoming));
    toast.success('Prize pack staged for every pending player.');
    setStep(3);
  };

  // ── Step 2 (Different) ─────────────────────────────────────────────────────
  const toggleCheckedForAssign = (userId) => {
    setCheckedForAssign((prev) => (
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    ));
  };

  const handleAssignGroup = () => {
    if (checkedForAssign.length === 0 || groupItemIds.length === 0) return;
    const incoming = checkedForAssign.flatMap((userId) => groupItemIds.map((itemId) => (
      { userId, itemId, quantity: 1, itemName: catalogItemLabel(catalogById.get(itemId)) }
    )));
    setPendingAssignments((prev) => mergeAssignments(prev, incoming));
    toast.success('Prize assigned.');
    setCheckedForAssign([]);
    setGroupItemIds([]);
  };

  const handleRemoveAssignment = (a) => {
    setPendingAssignments((prev) => prev.filter((p) => !(p.userId === a.userId && p.itemId === a.itemId)));
    toast.success('Assignment removed.');
  };

  const goReviewFromDifferent = () => {
    setStep(3);
  };

  // ── Step 3: read-only summary — whatever is listed here is exactly who gets granted;
  // no per-row opt-out, since this is the final confirmation before real orders/inventory
  // are created, not another editing step.
  const handleGrant = async () => {
    if (pendingPlayers.length === 0) return;
    setGranting(true);
    try {
      const userIds = pendingPlayers.map((p) => p.userId);
      // Strip the client-only itemName before sending — the server resolves its own display
      // name; only userId/itemId/quantity are part of the wire shape (AssignPrizeDto).
      const assignments = pendingAssignments.map(({ userId, itemId, quantity }) => ({ userId, itemId, quantity }));
      await grantPrizes(gameId, announcementId, userIds, assignments);
      toast.success(`${userIds.length} prize${userIds.length === 1 ? '' : 's'} granted.`);
      // The list behind this dialog must now show "winners granted" (Delete / quiz picker locked).
      onGranted?.();
      setPendingAssignments([]);
      await Promise.all([refreshPlayers(), refreshWinners()]);
      setStep(0);
      setMode(null);
    } catch (_) {
      toast.error('Failed to grant prizes. Nothing already granted was undone — check the Players list below before retrying.');
    } finally {
      setGranting(false);
    }
  };

  // Guards actually leaving the dialog (close button, backdrop click, escape) — NOT the
  // in-modal "Players" breadcrumb/Stepper navigation, which stays within the same session and
  // never loses pendingAssignments (only closing the whole modal does, since nothing is
  // persisted until Grant — see this file's top doc comment).
  const requestClose = () => {
    if (pendingAssignments.length > 0) {
      setConfirmCloseOpen(true);
      return;
    }
    onClose();
  };

  const confirmDiscardAndClose = () => {
    setConfirmCloseOpen(false);
    setPendingAssignments([]);
    onClose();
  };

  if (!announcement) return null;

  const detailPlayer = players.find((p) => p.userId === detailUserId) ?? null;
  // A Quiz announcement counts the points of its selected quizzes only; a Penalty one counts a date range.
  const quizzes = announcementQuizzes(announcement);
  const isQuiz = quizzes.length > 0;
  const scoreScope = isQuiz ? 'from the selected quizzes' : 'for this period';

  return (
    <Dialog
      open={open}
      onClose={requestClose}
      maxWidth="md"
      fullWidth
      fullScreen={isNarrow}
      PaperProps={{
        sx: {
          borderRadius: isNarrow ? 0 : '16px',
          border: `1px solid ${C.border}`,
          // Fixed, viewport-relative height on every open — regardless of step or how much
          // data this announcement has (zero eligible players vs. a full top-10 table).
          // Without this, MUI's default Paper sizing shrinks to fit whatever's shortest
          // (the "No scores recorded" message) and grows to fit whatever's tallest (a full
          // player table + banner), so the same dialog looks like two different components
          // depending on the announcement you open. Fixed height + DialogContent's own
          // overflow-y:auto (MUI default) means content scrolls internally instead.
          height: isNarrow ? '100%' : '85vh',
        },
      }}
    >
      <DialogContent sx={{ p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', gap: 2, ...thinScrollbarSx }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#1e293b' }}>
              <LocalText>{announcement.name || 'Announcement Details'}</LocalText>
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: C.muted, mt: 0.25 }}>
              <LocalText>{`Top ${announcement.topCount} eligible players`}</LocalText>
            </Typography>
            {isQuiz && (
              <Typography sx={{ fontSize: 12.5, color: C.muted, mt: 0.25 }}>
                <LocalText>Scores from</LocalText>: {quizzes.map((q) => q.name).join(', ')}
              </Typography>
            )}
          </Box>
          <IconButton onClick={requestClose} size="small" aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {step > 0 && (
          <>
            <Box
              onClick={() => { setStep(0); setMode(null); }}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 12.5, color: C.muted, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            >
              <Box component="span" fontWeight={700} sx={{ color: C.navy }}><LocalText>Players</LocalText></Box>
              <Box component="span">/ <LocalText>Assign Prizes</LocalText></Box>
            </Box>
            <Stepper step={step} onStepClick={(n) => setStep(n)} />
          </>
        )}

        {/* ── Step 0: merged Players list ───────────────────────────────── */}
        {step === 0 && (
          // flex + minHeight:0 fills the fixed-height dialog (see PaperProps above) rather
          // than sizing to content — so the loading/empty states below can center inside the
          // real leftover space instead of floating as a small label near the top of a now
          // much taller box.
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, gap: 2 }}>
            {playersLoading || winnersLoading || prizePackLoading ? (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" role="status" sx={{ color: C.muted }}>
                  <LocalText>Loading…</LocalText>
                </Typography>
              </Box>
            ) : players.length === 0 ? (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" role="status" sx={{ color: C.muted, textAlign: 'center' }}>
                  <LocalText>{`No scores recorded ${scoreScope} yet.`}</LocalText>
                </Typography>
              </Box>
            ) : (
              <>
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 1, bgcolor: '#eff6ff', borderRadius: 2,
                }}
                >
                  <Typography variant="body2" sx={{ fontSize: 12, color: C.navy, fontWeight: 600 }}>
                    <LocalText>{`This is always exactly the top ${players.length} players ${scoreScope} — it isn't a paged list of everyone who played. Fallback Prize Pack for anyone without an individual prize: `}</LocalText>
                    {prizePack.length ? prizePackLabel : <LocalText>(none set)</LocalText>}
                  </Typography>
                </Box>

                <TableContainer
                  sx={{
                    overflowX: 'auto',
                    border: `1px solid ${C.border}`,
                    borderRadius: '8px',
                    ...thinScrollbarSx,
                  }}
                >
                  <Table
                    size="small"
                    aria-label="Announcement players"
                    sx={{ '& tbody tr:last-of-type td': { borderBottom: 'none' } }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell scope="col" sx={headCellSx}><LocalText>Rank</LocalText></TableCell>
                        <TableCell scope="col" sx={headCellSx}><LocalText>Player</LocalText></TableCell>
                        <TableCell scope="col" sx={headCellSx}><LocalText>Email Address</LocalText></TableCell>
                        <TableCell scope="col" sx={headCellSx} align="right"><LocalText>Score</LocalText></TableCell>
                        <TableCell scope="col" sx={headCellSx}><LocalText>Last Played</LocalText></TableCell>
                        <TableCell scope="col" sx={headCellSx}><LocalText>Prize</LocalText></TableCell>
                        <TableCell scope="col" sx={headCellSx}><LocalText>Action</LocalText></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {players.map((p, idx) => {
                        const winner = winners.find((w) => w.userId === p.userId);
                        return (
                          <TableRow key={p.userId} hover selected={Boolean(winner)}>
                            <TableCell sx={bodyCellSx}><RankBadge rank={p.rank} /></TableCell>
                            <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{
                                  width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  bgcolor: entityColor(idx), color: '#fff', fontSize: 11.5, fontWeight: 700,
                                }}
                                >
                                  {initialOf(p.username || p.email)}
                                </Box>
                                {p.username}
                              </Box>
                            </TableCell>
                            <TableCell sx={bodyCellSx}>{p.email || '—'}</TableCell>
                            <TableCell sx={{ ...bodyCellSx, fontWeight: 700 }} align="right">{p.score}</TableCell>
                            <TableCell sx={bodyCellSx}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <AccessTimeIcon sx={{ fontSize: 14, color: C.muted }} />
                                {fmtDate(p.lastPlayedTime)}
                              </Box>
                            </TableCell>
                            <TableCell sx={bodyCellSx}>
                              {winner ? (
                                <Box>
                                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: C.navy }}>
                                    {winner.grantedItems || <LocalText>Granted</LocalText>}
                                  </Typography>
                                  <Typography sx={{ fontSize: 10.5, color: C.muted }}>{fmtDate(winner.grantedTime)}</Typography>
                                </Box>
                              ) : (
                                <Typography sx={{ fontSize: 12.5, color: '#cbd5e1' }}>
                                  <LocalText>Not granted yet</LocalText>
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={bodyCellSx}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setDetailUserId(p.userId)}
                                sx={{ ...btnBase, fontSize: 11.5, borderColor: C.border, color: C.navy, '&:hover': { borderColor: C.navy, bgcolor: '#eff6ff' } }}
                              >
                                <LocalText>Details</LocalText>
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {canManage && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={startWizard}
                  disabled={pendingPlayers.length === 0}
                  sx={{ ...btnBase, ...ctaNavy }}
                >
                  <LocalText>Assign Prizes</LocalText>
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* ── Step 1: Choose Mode ─────────────────────────────────────────── */}
        {step === 1 && (
          <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 800, color: C.navy }}>
              <LocalText>How do you want to give out prizes?</LocalText>
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: C.muted, mt: 0.5 }}>
              <LocalText>Pick one — you can always come back and redo this before granting.</LocalText>
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: isNarrow ? 'column' : 'row', gap: 2, mt: 2.5 }}>
              <ModeCard
                icon="="
                title="Same Prize for All Users"
                description={prizePack.length === 0
                  ? 'Set a Prize Pack first (Edit Announcement) — players can only be given items that were actually announced for this week.'
                  : `Every item in this announcement's Prize Pack goes to every not-yet-granted player, automatically — no picking needed. Applies to ${pendingPlayers.length} player${pendingPlayers.length === 1 ? '' : 's'}.`}
                onClick={() => chooseMode('same')}
                disabled={prizePack.length === 0}
              />
              <ModeCard
                icon="≠"
                title="Different Prizes for Different Users"
                description={prizePack.length === 0
                  ? 'Set a Prize Pack first (Edit Announcement) — players can only be given items that were actually announced for this week.'
                  : 'Pick a group of players and assign them one of this announcement\'s Prize Pack items. Repeat for other groups. Anyone left unpicked falls back to the Prize Pack.'}
                onClick={() => chooseMode('different')}
                disabled={prizePack.length === 0}
              />
            </Box>
          </Box>
        )}


        {/* ── Step 2 (Different): item picker on top, full player table below ── */}
        {step === 2 && mode === 'different' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.75 }}>
                <LocalText>1 · Pick the item(s) to give</LocalText>
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setGroupPickerOpen(true)}
                disabled={catalogLoading || prizePackCatalog.length === 0}
                sx={{ ...btnBase, borderColor: C.border, color: C.navy, '&:hover': { borderColor: C.navy, bgcolor: '#eff6ff' } }}
              >
                <LocalText>{groupItemIds.length > 0 ? `${groupItemIds.length} Item(s) Selected` : 'Select Item(s)…'}</LocalText>
              </Button>
            </Box>

            <Box>
              <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.75 }}>
                <LocalText>2 · Select players &amp; assign</LocalText>
              </Typography>

              <TableContainer sx={{ overflowX: 'auto', border: `1px solid ${C.border}`, borderRadius: 2, ...thinScrollbarSx }}>
                <Table size="small" sx={{ '& tbody tr:last-of-type td': { borderBottom: 'none' } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ ...headCellSx, px: 1 }}>
                        <Checkbox
                          size="small"
                          checked={checkedForAssign.length > 0 && checkedForAssign.length === pendingPlayers.length}
                          indeterminate={checkedForAssign.length > 0 && checkedForAssign.length < pendingPlayers.length}
                          onChange={(e) => setCheckedForAssign(e.target.checked ? pendingPlayers.map((p) => p.userId) : [])}
                          inputProps={{ 'aria-label': 'Select all pending players' }}
                        />
                      </TableCell>
                      <TableCell sx={headCellSx}><LocalText>Rank</LocalText></TableCell>
                      <TableCell sx={headCellSx}><LocalText>Player</LocalText></TableCell>
                      <TableCell sx={headCellSx}><LocalText>Email</LocalText></TableCell>
                      <TableCell sx={headCellSx} align="right"><LocalText>Score</LocalText></TableCell>
                      <TableCell sx={headCellSx}><LocalText>Assigned</LocalText></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingPlayers.map((p, idx) => {
                      const own = assignmentsByUser.get(p.userId) ?? [];
                      const checked = checkedForAssign.includes(p.userId);
                      return (
                        <TableRow key={p.userId} hover selected={checked}>
                          <TableCell padding="checkbox" sx={{ ...bodyCellSx, px: 1 }}>
                            <Checkbox size="small" checked={checked} onChange={() => toggleCheckedForAssign(p.userId)} inputProps={{ 'aria-label': `Select ${p.username}` }} />
                          </TableCell>
                          <TableCell sx={bodyCellSx}><RankBadge rank={p.rank} /></TableCell>
                          <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{
                                width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                bgcolor: entityColor(players.indexOf(p)), color: '#fff', fontSize: 11.5, fontWeight: 700,
                              }}
                              >
                                {initialOf(p.username || p.email)}
                              </Box>
                              {p.username}
                            </Box>
                          </TableCell>
                          <TableCell sx={bodyCellSx}>{p.email || '—'}</TableCell>
                          <TableCell sx={{ ...bodyCellSx, fontWeight: 700 }} align="right">{p.score}</TableCell>
                          <TableCell sx={bodyCellSx}>
                            {own.length === 0 ? (
                              <Typography sx={{ fontSize: 12, color: '#cbd5e1' }}>—</Typography>
                            ) : (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {own.map((a) => (
                                  <Box
                                    key={`${a.userId}:${a.itemId}`}
                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.4, pl: 1, pr: 0.4, py: 0.3, bgcolor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '999px', fontSize: 11, fontWeight: 700, color: C.navy }}
                                  >
                                    {a.itemName}
                                    <IconButton size="small" onClick={() => handleRemoveAssignment(a)} aria-label={`Remove ${a.itemName}`} sx={{ p: 0.2, color: C.navy, opacity: 0.6, '&:hover': { opacity: 1, color: '#dc2626' } }}>
                                      <CloseIcon sx={{ fontSize: 12 }} />
                                    </IconButton>
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography sx={{ fontSize: 11.5, color: C.muted, mt: 0.75 }}>
                <LocalText>Pick item(s) above, check players, click Assign. Players left unassigned fall back to the Prize Pack (if any) when granted, not to nothing.</LocalText>
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button variant="outlined" onClick={() => setStep(1)} sx={{ ...btnBase, borderColor: C.border, color: C.navy }}>
                <LocalText>Back</LocalText>
              </Button>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button
                  variant="contained"
                  onClick={handleAssignGroup}
                  disabled={checkedForAssign.length === 0 || groupItemIds.length === 0}
                  sx={{ ...btnBase, ...ctaNavy }}
                >
                  <LocalText>{`Assign to ${checkedForAssign.length} selected`}</LocalText>
                </Button>
                <Button variant="contained" onClick={goReviewFromDifferent} sx={{ ...btnBase, ...ctaNavy }}>
                  <LocalText>Next</LocalText>
                </Button>
              </Box>
            </Box>

            <PrizeItemPickerModal
              open={groupPickerOpen}
              onClose={() => setGroupPickerOpen(false)}
              catalog={prizePackCatalog}
              itemTypes={itemTypes}
              selectedIds={new Set(groupItemIds)}
              onToggle={(itemId) => setGroupItemIds((prev) => (prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]))}
              allowCreatePrice={false}
              onCreatePriceConfirmed={() => {}}
            />
          </Box>
        )}

        {/* ── Step 3: Review & Grant ─────────────────────────────────────── */}
        {/* Read-only summary, not another selection step — whoever is listed here is
            exactly who gets granted when the button below is clicked. Individual
            assignment/mode choices already happened in Steps 1-2; this is the final
            confirmation before real orders/inventory are created, so there's nothing left
            to pick — only to review and confirm or go Back. */}
        {step === 3 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box>
              <Typography sx={{ fontSize: 15, fontWeight: 800, color: C.navy }}>
                <LocalText>{`${pendingPlayers.length} player(s) will be granted`}</LocalText>
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: C.muted, mt: 0.25 }}>
                <LocalText>Double-check before granting — this creates real orders and inventory for every player listed below.</LocalText>
              </Typography>
            </Box>

            {prizePack.length > 0 && (
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 1, bgcolor: '#eff6ff', borderRadius: 2,
              }}
              >
                <Typography variant="body2" sx={{ fontSize: 12, color: C.navy, fontWeight: 600 }}>
                  <LocalText>{'A player with their own assigned prize gets exactly that. Everyone else (tagged "(Prize Pack)" below) falls back to the FULL Prize Pack bundle — every item in it, not just one: '}</LocalText>
                  {prizePackLabel}
                </Typography>
              </Box>
            )}

            <TableContainer sx={{ overflowX: 'auto', border: `1px solid ${C.border}`, borderRadius: 2, maxHeight: 360, ...thinScrollbarSx }}>
              <Table size="small" stickyHeader sx={{ '& tbody tr:last-of-type td': { borderBottom: 'none' } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}><LocalText>Rank</LocalText></TableCell>
                    <TableCell sx={headCellSx}><LocalText>Player</LocalText></TableCell>
                    <TableCell sx={headCellSx} align="right"><LocalText>Score</LocalText></TableCell>
                    <TableCell sx={headCellSx}><LocalText>Will Receive</LocalText></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingPlayers.map((p, idx) => {
                    const resolved = resolvedPrizeFor(p.userId);
                    return (
                      <TableRow key={p.userId} hover>
                        <TableCell sx={bodyCellSx}><RankBadge rank={p.rank} /></TableCell>
                        <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{
                              width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              bgcolor: entityColor(idx), color: '#fff', fontSize: 11.5, fontWeight: 700,
                            }}
                            >
                              {initialOf(p.username || p.email)}
                            </Box>
                            {p.username}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ ...bodyCellSx, fontWeight: 700 }} align="right">{p.score}</TableCell>
                        <TableCell sx={bodyCellSx}>
                          {resolved.label ? (
                            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: resolved.source === 'assignment' ? C.navy : '#334155' }}>
                              {resolved.label}
                              {resolved.source === 'prizePack' && (
                                <Typography component="span" sx={{ fontSize: 10.5, color: C.muted, fontWeight: 600, ml: 0.5 }}>
                                  <LocalText>(Prize Pack)</LocalText>
                                </Typography>
                              )}
                            </Typography>
                          ) : (
                            <Typography sx={{ fontSize: 12, color: '#dc2626' }}>
                              <LocalText>No prize — recognition only</LocalText>
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button variant="outlined" onClick={() => setStep(2)} sx={{ ...btnBase, borderColor: C.border, color: C.navy }}>
                <LocalText>Back</LocalText>
              </Button>
              <Button
                variant="contained"
                onClick={handleGrant}
                disabled={granting || pendingPlayers.length === 0}
                sx={{ ...btnBase, bgcolor: '#059669', color: '#fff', '&:hover': { bgcolor: '#047857' } }}
              >
                <LocalText>{granting ? 'Granting…' : `Grant Prizes (${pendingPlayers.length})`}</LocalText>
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      {isQuiz ? (
        <AnnouncementPlayerBreakdownModal
          open={Boolean(detailPlayer)}
          onClose={() => setDetailUserId(null)}
          gameId={gameId}
          announcementId={announcementId}
          row={detailPlayer}
        />
      ) : (
        <ScoreboardDetailModal
          open={Boolean(detailPlayer)}
          onClose={() => setDetailUserId(null)}
          row={detailPlayer}
          gameId={gameId}
          filter="custom"
          from={announcement.startDate}
          to={announcement.endDate}
          rangeFrom={announcement.startDate}
          rangeTo={announcement.endDate}
        />
      )}

      <ConfirmModal
        open={confirmCloseOpen}
        onCancel={() => setConfirmCloseOpen(false)}
        onConfirm={confirmDiscardAndClose}
        title="Discard prize assignments?"
        message="You have prize assignments that haven't been granted yet."
        subtitle="Leaving now will discard them — you'll need to redo the picks next time."
        confirmLabel="Leave & Discard"
        cancelLabel="Stay"
        severity="warning"
      />
    </Dialog>
  );
}
